// src/components/ReportDialog.jsx
// Report PDF del pilota per una gara o un allenamento salvati: cosa includere,
// note dell'istruttore e marchio di chi lo consegna (nome, logo, colore,
// contatti — si salvano per tutti i report di questa copia), poi il server
// impagina e stampa il PDF. La finestra resta sempre dentro lo schermo
// (testata e pulsanti fissi, il contenuto scorre) e durante la generazione
// mostra una barra di avanzamento.
//   endpoint  percorso /api/... che genera il PDF (POST { notes })
//   name      pilota (o auto) del report
//   kind      'race' | 'training'
//   stints    allenamento con le auto: turni di guida dell'auto, da scegliere
//             con le caselle (initialStints = quelli già spuntati, di default tutti)
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { stintPilotName, fmtClock, fmtLapShort } from '../utils/crew';
import './ReportDialog.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const SWATCHES = ['#e10600', '#c9a227', '#1f6feb', '#12a150', '#7c3aed', '#0d0f14'];

const COPY = {
  race: {
    intro: 'Analisi della gara pronta da consegnare: tempi, giro ideale, costanza, settori, velocità lungo il giro e curva per curva a confronto con il più veloce, priorità per migliorare.',
    placeholder: 'Es. Ottimo passo nella seconda metà. Prossima sessione: lavoriamo sull\'ingresso della curva 6.',
    pages: 'PDF A4, 4–6 pagine',
    steps: ['Raccolta dei giri', 'Analisi delle traiettorie', 'Grafici e confronti', 'Impaginazione del PDF'],
  },
  training: {
    intro: 'Analisi della sessione pronta da consegnare all\'allievo: giri e progressione, giro ideale, confronto con l\'istruttore (o con il più veloce), traiettorie sulla foto satellitare, forze G e priorità per la prossima sessione.',
    placeholder: 'Es. Buona progressione nella seconda parte. Prossima sessione: staccata della curva 2 più decisa e gas anticipato in uscita dalla 6.',
    pages: 'PDF A4, 7–9 pagine',
    steps: ['Raccolta dei giri', 'Analisi delle traiettorie', 'Meteo e grafici', 'Impaginazione del PDF'],
  },
};

// Avanzamento stimato (il server risponde solo alla fine): sale veloce e poi
// rallenta verso il 92%; alla risposta va al 100%.
const estimate = (ms) => 92 * (1 - Math.exp(-ms / 3500));

export default function ReportDialog({ endpoint, name, kind = 'race', onClose, stints = null, initialStints = null }) {
  const copy = COPY[kind] || COPY.race;
  const stintList = kind === 'training' && Array.isArray(stints) && stints.length ? stints : null;
  const [picked, setPicked] = useState(() => {
    if (!stintList) return [];
    const all = stintList.map(s => Number(s.n));
    const want = (Array.isArray(initialStints) ? initialStints : []).map(Number).filter(n => all.includes(n));
    return want.length ? want : all;
  });
  const togglePick = (n) => setPicked(p => (p.includes(n) ? p.filter(x => x !== n) : [...p, n].sort((a, b) => a - b)));
  const pickedNames = stintList ? [...new Set(stintList.filter(s => picked.includes(Number(s.n))).map(stintPilotName))] : [];
  const pickedLaps = stintList ? stintList.filter(s => picked.includes(Number(s.n))).reduce((a, s) => a + (Number(s.laps) || 0), 0) : null;
  const [notes, setNotes] = useState('');
  // allenamento: commenti e consigli scritti dalla piattaforma (di default sì)
  const [advice, setAdvice] = useState(true);
  const [brand, setBrand] = useState(null);       // impostazioni salvate
  const [draft, setDraft] = useState(null);       // modifiche in corso
  const [showBrand, setShowBrand] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [error, setError] = useState('');
  // generazione: 'form' → 'busy' (barra di avanzamento) → 'done' (scaricato)
  const [phase, setPhase] = useState('form');
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState(null);         // { name, url, kb }
  const busy = phase === 'busy';
  const fileRef = useRef(null);
  const startRef = useRef(0);

  // marchio salvato: una volta sola all'apertura (le modifiche in corso non
  // devono sparire se la pagina sotto si aggiorna)
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/report-settings`).then(r => (r.ok ? r.json() : null)).then(j => {
      if (!alive || !j) return;
      setBrand(j); setDraft({ brandName: j.brandName, accent: j.accent, contact: j.contact });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  // barra di avanzamento durante la generazione
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(() => setProgress(p => Math.max(p, estimate(Date.now() - startRef.current))), 120);
    return () => clearInterval(t);
  }, [busy]);

  // il file scaricato resta disponibile ("Scarica di nuovo") finché la finestra è aperta
  useEffect(() => () => { if (file?.url) URL.revokeObjectURL(file.url); }, [file]);

  const brandChanged = brand && draft && (draft.brandName !== brand.brandName || draft.accent !== brand.accent || draft.contact !== brand.contact);

  const saveBrand = async () => {
    if (!brandChanged) return true;
    setSavingBrand(true);
    try {
      const r = await fetch(`${API_BASE}/api/report-settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft) });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.error || 'Salvataggio non riuscito');
      setBrand(j); setDraft({ brandName: j.brandName, accent: j.accent, contact: j.contact });
      return true;
    } catch (e) { setError(e.message); return false; } finally { setSavingBrand(false); }
  };

  const uploadLogo = async (f) => {
    if (!f) return;
    setError('');
    const fd = new FormData(); fd.append('logo', f);
    try {
      const r = await fetch(`${API_BASE}/api/report-settings/logo`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.error || 'Caricamento non riuscito');
      setBrand(j);
    } catch (e) { setError(e.message); }
  };
  const removeLogo = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/report-settings/logo`, { method: 'DELETE' });
      const j = await r.json().catch(() => null);
      if (r.ok) setBrand(j);
    } catch { /* noop */ }
  };

  const download = (f) => {
    const a = document.createElement('a');
    a.href = f.url; a.download = f.name; document.body.appendChild(a); a.click(); a.remove();
  };

  const generate = async () => {
    setError('');
    if (stintList && !picked.length) { setError('Scegli almeno un turno di guida.'); return; }
    if (!(await saveBrand())) return;
    startRef.current = Date.now();
    setProgress(2);
    setPhase('busy');
    try {
      const body = kind === 'training' ? { notes, advice, ...(stintList ? { stints: picked } : {}) } : { notes };
      const r = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => null);
        throw new Error(j?.error || 'Generazione non riuscita');
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const f = { name: /filename="([^"]+)"/.exec(cd)?.[1] || 'report.pdf', url: URL.createObjectURL(blob), kb: Math.round(blob.size / 1024) };
      setProgress(100);
      setFile(f);   // il file precedente lo libera l'effetto qui sopra
      download(f);
      setTimeout(() => setPhase('done'), 450);
    } catch (e) {
      setError(e.message || 'Generazione non riuscita');
      setPhase('form');
    }
  };

  const logoSrc = brand?.logoUrl ? `${API_BASE}${brand.logoUrl}` : null;
  const stepIdx = Math.min(copy.steps.length - 1, Math.floor(progress / (92 / copy.steps.length)));
  const title = stintList && pickedNames.length ? pickedNames.join(', ') : name;

  return (
    <div className="rs-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="rs-modal rs-modal--lg rp" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="rp-title">
        <div className="rs-modal__head">
          <div className="rp__head">
            <span className="rs-kicker rs-kicker--red">Report PDF</span>
            <div className="rs-modal__title" id="rp-title">{title}</div>
            {stintList && <div className="rp__sub">{name}</div>}
          </div>
          <button className="rs-btn rs-btn--icon rs-btn--ghost" onClick={onClose} disabled={busy} aria-label="Chiudi"><Icon.X size={18} /></button>
        </div>

        <div className="rs-modal__body rp__body">
          {phase === 'busy' && (
            <div className="rp__progress" role="status" aria-live="polite">
              <div className="rp__progress-icon"><span className="rs-spinner" /></div>
              <strong>Preparazione del report di {title}</strong>
              <div className="rp__bar" aria-hidden="true"><span style={{ width: `${progress.toFixed(1)}%` }} /></div>
              <div className="rp__progress-meta">
                <span>{progress >= 100 ? 'Download del PDF' : copy.steps[stepIdx]}…</span>
                <b>{Math.round(progress)}%</b>
              </div>
              <ol className="rp__steps">
                {copy.steps.map((s, i) => (
                  <li key={s} className={progress >= 100 || i < stepIdx ? 'is-done' : i === stepIdx ? 'is-now' : ''}>{s}</li>
                ))}
              </ol>
              <small>Non chiudere la finestra: il PDF si scarica da solo appena è pronto.</small>
            </div>
          )}

          {phase === 'done' && file && (
            <div className="rp__progress rp__progress--done" role="status">
              <div className="rp__progress-icon rp__progress-icon--ok"><Icon.Check size={22} /></div>
              <strong>Report scaricato</strong>
              <div className="rp__bar is-full" aria-hidden="true"><span style={{ width: '100%' }} /></div>
              <div className="rp__file"><Icon.Archive size={14} /><span>{file.name}</span><em>{file.kb} KB</em></div>
              <small>Se il download non è partito, usa «Scarica di nuovo».</small>
            </div>
          )}

          {phase === 'form' && (
            <>
              <p className="rp__intro">{copy.intro}</p>
              {error && <div className="rs-alert rs-alert--danger"><Icon.Alert /><span>{error}</span></div>}

              {kind === 'training' && (
                <section className="rp__sec">
                  <h3 className="rp__sec-title"><span>1</span>Cosa includere</h3>
                  {stintList && (
                    <div className="rp__stints">
                      <div className="rp__stints-head">
                        <strong>Turni di guida</strong>
                        <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm"
                          onClick={() => setPicked(picked.length === stintList.length ? [] : stintList.map(s => Number(s.n)))}>
                          {picked.length === stintList.length ? 'Nessuno' : 'Tutti'}
                        </button>
                      </div>
                      {stintList.map(s => {
                        const on = picked.includes(Number(s.n));
                        const to = s.to ? fmtClock(s.to) : 'in corso';
                        return (
                          <label key={s.n} className={`rp__stint ${on ? 'is-on' : ''}`}>
                            <input type="checkbox" checked={on} onChange={() => togglePick(Number(s.n))} />
                            <span className="rp__check-box" aria-hidden="true" />
                            <span className="rp__stint-n">{s.n}</span>
                            <span className="rp__stint-who"><strong>{stintPilotName(s)}</strong><small>{fmtClock(s.from)} – {to}</small></span>
                            <span className="rp__stint-num"><b>{s.laps ?? 0}</b> {s.laps === 1 ? 'giro' : 'giri'}</span>
                            <span className="rp__stint-num">best <b>{fmtLapShort(s.bestLapTime ?? s.best)}</b></span>
                          </label>
                        );
                      })}
                      <small className="rp__stints-note">Il report analizza solo i giri dei turni spuntati; il giro dell'istruttore resta come riferimento.</small>
                    </div>
                  )}
                  <label className={`rp__check ${advice ? 'is-on' : ''}`}>
                    <input type="checkbox" checked={advice} onChange={e => setAdvice(e.target.checked)} />
                    <span className="rp__check-box" aria-hidden="true" />
                    <span className="rp__check-text">
                      <strong>Commenti e consigli automatici</strong>
                      <small>{advice
                        ? 'Il report include le osservazioni, la lettura delle curve e le priorità per la prossima sessione.'
                        : 'Il report riporta solo i dati: al posto di osservazioni, lettura delle curve e priorità restano spazi per la spiegazione dell\'istruttore.'}</small>
                    </span>
                  </label>
                </section>
              )}

              <section className="rp__sec">
                <h3 className="rp__sec-title"><span>{kind === 'training' ? 2 : 1}</span>Note dell'istruttore <em className="rp__opt">facoltative · stampate nel report</em></h3>
                <textarea className="rs-textarea" rows={3} maxLength={2000} value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder={copy.placeholder} aria-label="Note dell'istruttore" />
              </section>

              <section className="rp__sec">
                <h3 className="rp__sec-title"><span>{kind === 'training' ? 3 : 2}</span>Marchio del report</h3>
                <div className="rp__brand">
                  <button type="button" className="rp__brand-head" onClick={() => setShowBrand(v => !v)} aria-expanded={showBrand}>
                    <span className="rp__brand-preview" style={{ '--acc': draft?.accent || '#e10600' }}>
                      {logoSrc ? <img src={logoSrc} alt="" /> : brand?.defaultLogo ? <span className="rp__brand-default">Logo predefinito</span> : <b>{draft?.brandName || '—'}</b>}
                    </span>
                    <span className="rp__brand-text">
                      <strong>{draft?.brandName || 'Il tuo marchio'}</strong>
                      <small>Nome, logo, colore e contatti stampati nel report. Valgono per tutti i report.</small>
                    </span>
                    <span className="rp__brand-edit">{showBrand ? 'Chiudi' : 'Modifica'}<Icon.ChevronDown size={16} className={showBrand ? 'is-open' : ''} /></span>
                  </button>
                  {showBrand && draft && (
                    <div className="rp__brand-body">
                      <div className="rs-grid rs-grid--2">
                        <label className="rs-field">
                          <span className="rs-label">Nome</span>
                          <input className="rs-input" value={draft.brandName} maxLength={60} onChange={e => setDraft(d => ({ ...d, brandName: e.target.value }))} />
                        </label>
                        <label className="rs-field">
                          <span className="rs-label">Contatti a fine report</span>
                          <input className="rs-input" value={draft.contact} maxLength={140} placeholder="www.sito.it · info@sito.it · +39 …" onChange={e => setDraft(d => ({ ...d, contact: e.target.value }))} />
                        </label>
                      </div>
                      <div className="rp__row">
                        <div className="rs-field">
                          <span className="rs-label">Colore</span>
                          <div className="rp__swatches">
                            {SWATCHES.map(c => (
                              <button key={c} type="button" className={`rp__swatch${draft.accent.toLowerCase() === c ? ' is-active' : ''}`} style={{ background: c }} onClick={() => setDraft(d => ({ ...d, accent: c }))} aria-label={`Colore ${c}`} />
                            ))}
                            <label className="rp__custom" title="Altro colore">
                              <input type="color" value={draft.accent} onChange={e => setDraft(d => ({ ...d, accent: e.target.value }))} />
                              <span>Altro</span>
                            </label>
                          </div>
                        </div>
                        <div className="rs-field">
                          <span className="rs-label">Logo</span>
                          <div className="rp__logo">
                            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={e => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} />
                            <button type="button" className="rs-btn rs-btn--sm" onClick={() => fileRef.current?.click()}>{logoSrc ? 'Cambia logo' : 'Carica logo'}</button>
                            {logoSrc && <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={removeLogo}>Rimuovi</button>}
                            <small>PNG, JPG, WEBP o SVG, meglio su fondo trasparente. Se il logo è bianco, sulle pagine chiare viene stampato scuro.</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>

        <div className="rs-modal__foot rp__foot">
          {phase === 'done' ? (
            <>
              <span className="rp__hint">Puoi cambiare le scelte e generarne un altro.</span>
              <button className="rs-btn rs-btn--ghost" onClick={() => { setPhase('form'); setProgress(0); }}>Nuovo report</button>
              <button className="rs-btn" onClick={() => download(file)}><Icon.Download size={15} /> Scarica di nuovo</button>
              <button className="rs-btn rs-btn--primary" onClick={onClose}>Chiudi</button>
            </>
          ) : (
            <>
              <span className="rp__hint">
                {busy ? 'Di solito bastano pochi secondi'
                  : stintList ? `${copy.pages} · ${pickedLaps} ${pickedLaps === 1 ? 'giro' : 'giri'} nei turni scelti` : copy.pages}
              </span>
              <button className="rs-btn rs-btn--ghost" onClick={onClose} disabled={busy}>Annulla</button>
              <button className="rs-btn rs-btn--primary" onClick={generate} disabled={busy || savingBrand || (!!stintList && !picked.length)}>
                {busy ? <><span className="rs-spinner" /> Generazione…</> : <><Icon.Download size={15} /> Genera e scarica PDF</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
